import { fireArtifactAction } from "@/components/artifacts/ArtifactActionsButtons.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation, useSearch } from "wouter";

const ACT_PARAM = "act";

/** Parse `?act=<messageId>:<actionId>:<artifactId>` (artifactId may contain ":"). */
function parseAct(
  search: string,
): { messageId: number; actionId: string; artifactId: string } | null {
  const raw = new URLSearchParams(search).get(ACT_PARAM);
  if (!raw) return null;
  const parts = raw.split(":");
  if (parts.length < 3) return null;
  const messageId = Number(parts[0]);
  const actionId = parts[1];
  const artifactId = parts.slice(2).join(":");
  if (!Number.isInteger(messageId) || !actionId || !artifactId) return null;
  return { messageId, actionId, artifactId };
}

/**
 * One-click action from the approval email (BIG-5585). When the thread URL
 * carries `?act=<messageId>:<actionId>:<artifactId>`, fetch that artifact and
 * fire its primary action once — but only if it's still awaiting the user and
 * the primary POST action still matches. A stale, already-handled, or
 * mismatched link is a no-op, so a refresh or a double-clicked link never
 * double-fires. Then strip the param so the URL is clean. Runs in the user's
 * own session via the same path as the in-app button — no token/endpoint, and
 * a link prefetch (no JS) can't trigger it.
 */
export function useEmailAutoAction(threadId: number): void {
  const search = useSearch();
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const parsed = parseAct(search);
    if (!parsed || !Number.isInteger(threadId)) return;

    const key = `${threadId}:${parsed.messageId}:${parsed.actionId}:${parsed.artifactId}`;
    if (handledRef.current === key) return; // already fired this action

    let cancelled = false;
    const stripParam = () => {
      const params = new URLSearchParams(search);
      params.delete(ACT_PARAM);
      const qs = params.toString();
      // Include the path explicitly rather than relying on relative resolution.
      setLocation(qs ? `${location}?${qs}` : location, { replace: true });
    };

    void (async () => {
      try {
        const artifact = await queryClient.fetchQuery(
          artifactQueries.detail(threadId, parsed.messageId, parsed.artifactId),
        );
        if (cancelled) return;
        const primary = "actions" in artifact ? artifact.actions?.[0] : undefined;
        const isWaiting = "status" in artifact && artifact.status === "waitingForUserAction";
        if (isWaiting && primary?.method === "POST" && primary.id === parsed.actionId) {
          // Mark handled only here, at the commit point — not before the async.
          // StrictMode mounts the effect twice in dev; the first pass is
          // cancelled before its fetch resolves, so setting the guard up front
          // made the real second pass skip and the action never fired.
          handledRef.current = key;
          await fireArtifactAction(primary);
          await queryClient.invalidateQueries({ queryKey: artifactQueries.all() });
        }
      } catch {
        if (!cancelled) {
          errorToast("Couldn't complete that from the email — open the item to act.");
        }
      } finally {
        if (!cancelled) stripParam();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [search, location, threadId, queryClient, setLocation]);
}
